import React, { createContext, useContext, useEffect, useState } from 'react';
import { AccountInfo, PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Client } from '@microsoft/microsoft-graph-client';
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { CALENDAR_EDITORS_GROUP_ID, loginRequest } from '../authConfig';

interface AuthContextType {
    user: AccountInfo | null;
    isAuthenticated: boolean;
    isCalendarEditor: boolean;
    graphClient: Client | null;
    login: () => Promise<void>;
    logout: () => void;
    loading: boolean;
    isBypassMode: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
    msalInstance: PublicClientApplication;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, msalInstance }) => {
    const { instance, accounts, inProgress } = useMsal();
    const [isCalendarEditor, setIsCalendarEditor] = useState(false);
    const [graphClient, setGraphClient] = useState<Client | null>(null);
    const [loading, setLoading] = useState(true);

    // Check if bypass mode is enabled
    const isBypassMode = process.env.REACT_APP_DISABLE_LOGIN === 'true';

    const user = accounts[0] || null;
    const isAuthenticated = isBypassMode || !!user;

    useEffect(() => {
        const initializeAuth = async () => {
            if (isBypassMode) {
                // In bypass mode, grant editor permissions but no graph client
                setIsCalendarEditor(true);
                setGraphClient(null);
                setLoading(false);
            } else if (isAuthenticated && user) {
                try {
                    // Create Graph client
                    const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(msalInstance, {
                        account: user,
                        scopes: loginRequest.scopes || [],
                        interactionType: InteractionType.Popup,
                    });

                    const client = Client.initWithMiddleware({ authProvider });
                    setGraphClient(client);

                    // Check if user is in CalendarEditors group
                    await checkCalendarEditorRole(client);
                } catch (error) {
                    console.error('Error initializing auth:', error);
                }
                setLoading(false);
            } else {
                setGraphClient(null);
                setIsCalendarEditor(false);
                setLoading(false);
            }
        };

        if (isBypassMode || inProgress === 'none') {
            initializeAuth();
        }
    }, [isAuthenticated, user, instance, inProgress, isBypassMode, msalInstance]);

    const checkCalendarEditorRole = async (client: Client) => {
        try {
            const groups = await client.api('/me/memberOf').get();
            const isEditor = groups.value.some((group: any) =>
                group.id === CALENDAR_EDITORS_GROUP_ID
            );
            setIsCalendarEditor(isEditor);
        } catch (error) {
            console.error('Error checking group membership:', error);
            setIsCalendarEditor(false);
        }
    };

    const login = async () => {
        try {
            await instance.loginPopup(loginRequest);
        } catch (error) {
            console.error('Login failed:', error);
        }
    };

    const logout = () => {
        instance.logoutPopup({
            account: user,
        });
    };

    const value: AuthContextType = {
        user: isBypassMode ? { name: 'Guest User', username: 'guest@company.com' } as AccountInfo : user,
        isAuthenticated,
        isCalendarEditor,
        graphClient,
        login,
        logout,
        loading,
        isBypassMode,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
